import { useCallback, memo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRoleTemplates } from '@/hooks/useRoleTemplates';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import {
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  Users,
  FileText,
  ShieldCheck,
  ClipboardList
} from 'lucide-react';

const RoleTemplateRow = memo(function RoleTemplateRow({ template, onEdit, onDelete, hasEditPermission }) {
  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full shrink-0"
            style={{ backgroundColor: template.color || '#6366f1' }}
          />
          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-gray-900">{template.name}</span>
              {template.job_profile && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <ClipboardList className="h-3.5 w-3.5 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Has job profile</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            {template.description && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-sm text-muted-foreground line-clamp-1 max-w-md">
                      {template.description}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm">
                    <p>{template.description}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1.5 cursor-default">
                <FileText className="h-3.5 w-3.5 text-gray-400" />
                <span className="text-sm font-medium">{template.requirementCount}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <div className="text-xs space-y-1">
                <div className="font-semibold border-b pb-1 mb-1">Courses</div>
                {template.requirements?.length > 0 ? (
                  template.requirements.map(req => (
                    <div key={req.course_name} className="flex items-center gap-2">
                      <span className="truncate">{req.course_name}</span>
                      {req.course_link_type && (
                        <span className="text-gray-400 shrink-0 capitalize">({req.course_link_type})</span>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-gray-400">No courses</div>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5 text-gray-400" />
          <span className="text-sm font-medium">{template.connectedUsers}</span>
        </div>
      </TableCell>
      <TableCell>
        <Badge
          variant="outline"
          className="text-[10px] py-0 px-2"
          style={{
            backgroundColor: (template.color || '#6366f1') + '15',
            borderColor: (template.color || '#6366f1') + '40',
            color: template.color || '#6366f1'
          }}
        >
          {template.name}
        </Badge>
      </TableCell>
      <TableCell className="text-right">
        {hasEditPermission && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(template)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDelete(template)} className="text-red-600">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TableCell>
    </TableRow>
  );
});

export function RolesTab({ searchQuery = '', onEdit, onDelete }) {
  const { userProfile, canEditTraining } = useAuth();
  const hasEditPermission = canEditTraining();

  const { templates, loading } = useRoleTemplates(userProfile?.company_id, {
    enabled: !!userProfile?.company_id
  });

  const filteredTemplates = searchQuery.trim()
    ? templates.filter(t =>
        t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : templates;

  const handleEdit = useCallback((template) => {
    onEdit(template);
  }, [onEdit]);

  const handleDelete = useCallback((template) => {
    onDelete(template);
  }, [onDelete]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (filteredTemplates.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <ShieldCheck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">
            {searchQuery
              ? 'No role templates found matching your search'
              : hasEditPermission
                ? 'No role templates created yet. Create one to quickly assign sets of qualifications to users.'
                : 'No role templates have been created yet.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="rounded-xl bg-white shadow-xs ring-1 ring-gray-200 overflow-hidden">
      <Table className="table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[35%]">Role Template</TableHead>
            <TableHead className="w-[15%]">Requirements</TableHead>
            <TableHead className="w-[15%]">Users</TableHead>
            <TableHead className="w-[20%]">Badge Preview</TableHead>
            <TableHead className="w-[15%] text-right"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredTemplates.map(template => (
            <RoleTemplateRow
              key={template.id}
              template={template}
              onEdit={handleEdit}
              onDelete={handleDelete}
              hasEditPermission={hasEditPermission}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default RolesTab;
